import Modal from "@/components/atoms/Modal";
import SoftBadge from "@/components/atoms/SoftBadge";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_NAME_MESSAGE,
} from "@/constants/validatorMessages";
import { CompanyCourseStatusForm } from "@/interfaces/CompanyCourseStatus";
import { IModalMethods, IModalProps } from "@/interfaces/Modal";
import { useStoreCompanyCourseStatusMutation } from "@/services/companies/courses/statuses";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface StoreCompanyCourseStatusModalProps
  extends Omit<IModalProps, "children" | "onPress"> {}

const StoreCompanyCourseStatusModal: ForwardRefRenderFunction<
  IModalMethods,
  StoreCompanyCourseStatusModalProps
> = ({ onHide, ...props }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [storeCompanyStatusAsync] = useStoreCompanyCourseStatusMutation();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<CompanyCourseStatusForm>({
    defaultValues: {
      backgroundColor: "#1e1a55",
      textColor: "#ffffff",
    },
  });

  const onSubmit: SubmitHandler<CompanyCourseStatusForm> = async (data) => {
    await toast.promise(storeCompanyStatusAsync(data).unwrap, {
      pending: "Ajout du statut en cours...",
      success: {
        render() {
          return "Le statut a bien été ajouté 🤩";
        },
      },
      error: {
        render({}: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
    reset();
    showOrHiddenModal();
  };

  const onError: SubmitErrorHandler<CompanyCourseStatusForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      {...props}
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
      beforeFooter={
        <Controller
          control={control}
          name="backgroundColor"
          render={({ field: backgroundColor }) => (
            <Controller
              control={control}
              name="textColor"
              render={({ field: textColor }) => (
                <Controller
                  control={control}
                  name="name"
                  render={({ field: name }) => (
                    <SoftBadge
                      className="me-auto"
                      textClassName="h6"
                      bg={backgroundColor.value}
                      textColor={textColor.value}
                    >
                      {name.value ?? "Mon statut"}
                    </SoftBadge>
                  )}
                />
              )}
            />
          )}
        />
      }
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : En attente"
              type="text"
              {...register("name", {
                required: {
                  value: true,
                  message: INVALID_NAME_MESSAGE,
                },
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
                maxLength: {
                  value: 255,
                  message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Séléctionne la couleur du texte{" "}
              <span className="text-danger">*</span> :
            </Form.Label>{" "}
            <Form.Control
              type="color"
              {...register("textColor", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.textColor}
            />
            <Form.Control.Feedback type="invalid">
              {errors.textColor?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {" "}
              Séléctionne la couleur de fond{" "}
              <span className="text-danger">*</span> :
            </Form.Label>{" "}
            <Form.Control
              type="color"
              {...register("backgroundColor", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.backgroundColor}
            />
            <Form.Control.Feedback type="invalid">
              {errors.backgroundColor?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreCompanyCourseStatusModal);
